import { Point2D } from "../geometry/geometry";
import { Polygon, Side } from "../geometry/polygon";
import { BinarySearchTree } from "../util/binary-search-tree";
import { IntersectionSweeper, polygonsToSegments, Segment, segmentHorizontalPositionComparator, SweepMessage } from "./intersection-sweeper";

/**
* returns true if the given collection of sets belong to the set that this setSelector representsi
*/
export type SetSelector = (setsCollection: number[]) => boolean;

const union = (sets: number[]): SetSelector => {
	return someIn(sets);
}

const intersection = (sets: number[]): SetSelector => {
	return allIn(sets);
}

const symetric = (sets0: number[], sets1: number[]): SetSelector => {
	const complement0 = complement(sets0, sets1);
	const complement1 = complement(sets1, sets0);
	return (setsAtLineSide: number[]) => {
		return complement0(setsAtLineSide) || complement1(setsAtLineSide);
	}
}

const complement = (includedSets: number[], excludedSets: number[]): SetSelector => {
	const included = someIn(includedSets);
	const excluded = allOut(excludedSets);
	return (setsAtLineSide: number[]) => {
		return included(setsAtLineSide) && excluded(setsAtLineSide);
	}
}

const someIn = (includedSets: number[]): SetSelector => {
	const lInc = includedSets.length;
	return (setsAtLineSide: number[]) => {
		let oneIncludedFound = false;
		let i = 0;
		while (!oneIncludedFound && i < lInc) {
			oneIncludedFound = setsAtLineSide.indexOf(includedSets[i++]) !== -1;
		}
		return oneIncludedFound;
	}
}

const allIn = (includedSets: number[]): SetSelector => {
	const lInc = includedSets.length;
	return (setsAtLineSide: number[]) => {
		let allIncludedFound = true;
		let i = 0;
		while (allIncludedFound && i < lInc) {
			allIncludedFound = allIncludedFound && setsAtLineSide.indexOf(includedSets[i++]) !== -1;
		}
		return allIncludedFound;
	}
}

const allOut = (excludedSets: number[]): SetSelector => {
	const lExc = excludedSets.length;
	return (setsAtLineSide: number[]) => {
		let allExcludedNotFound = true;
		let i = 0;
		while (allExcludedNotFound && i < lExc) {
			allExcludedNotFound = allExcludedNotFound && setsAtLineSide.indexOf(excludedSets[i++]) === -1;
		}
		return allExcludedNotFound;
	}
}

export class SetSweeper {
	public vertices: Point2D[];
	private traversalGraph: TraversalGraph;
	private setIndices: number[];

	public constructor(polygons: Polygon[]) {
		this.traversalGraph = new TraversalGraph();

		const { vertices, segments } = polygonsToSegments(polygons);
		this.vertices = vertices;

		const intersectionSweeper = new IntersectionSweeper(vertices, segments, (message: SweepMessage) => {
			this.traversalGraph.addVertex(message.event.vertexIndex, message.segments, vertices);
		});
		intersectionSweeper.sweep();

		this.traversalGraph.applyDistribution(vertices);

		this.setIndices = [...Array(polygons.length).keys()];

		//console.log('' + this.traversalGraph);

	}

	public getUnion(included?: number[]): Polygon {
		return this.getPolygon(union(included ? included : this.setIndices));
	}
	public getIntersection(included?: number[]): Polygon {
		return this.getPolygon(intersection(included ? included : this.setIndices));
	}
	public getComplement( included?: number[], excluded?: number[]): Polygon {
		return this.getPolygon(complement(included ? included : this.setIndices, excluded ? excluded : []));
	}
	public getSymetric(set0: number[], set1: number[]): Polygon {
		return this.getPolygon(symetric(set0, set1));
	}

	public getPolygon(setSelector: SetSelector): Polygon {
		const edges = this.traversalGraph.getEdges(setSelector);
		//console.log(edges.map( edge => ''+edge.i0+'->'+edge.i1).join(', '));
		if (edges.length > 0) {
			const paths: number[][] = [];
			while (edges.length > 0) {
				const path: number[] = [];
				paths.push(path);
				path[0] = edges[0].i0;
				this.extendPath(path,0,edges);
				path.pop();//last element equals first element
				//console.log(' emitted path: ' + path.join(', '));
			}
			return new Polygon(this.vertices, paths);
		} else {
			return undefined;
		}
	}

	private extendPath(path: number[], insertAfterIndex: number, edges: SetEdge[]): number {
		const nextEdgeIndex: number = edges.findIndex(edgeEntry => edgeEntry.i0 === path[insertAfterIndex]);
		if (nextEdgeIndex !== -1) {
			const edge = edges.splice(nextEdgeIndex, 1)[0];
			path.splice(insertAfterIndex + 1, 0, edge.i1);
			
     		if(edges.findIndex(edgeEntry => edgeEntry.i0 === path[insertAfterIndex]) !== -1 ){
	          //another edges starts from this vertex -> a hole or a adjoining piece
				insertAfterIndex = this.extendPath(path,insertAfterIndex,edges);
           }
			return this.extendPath(path, insertAfterIndex+1, edges);
		}
		return insertAfterIndex;
	}
}

interface SetSegment extends Segment {
	leftSets: number[],// list of all sets/polygons at the left side of this segment
	rightSets: number[],// list of all sets/polygons at the right side of this segment
}
/**
* A segment is an actual edge of the set looked for if one side is part of the set and the other is not. I.E. setIsAtTheLeft != setIsAtTheRight
*/
interface SetEdge extends SetSegment {
	setIsAtTheLeft: boolean; //true when the set searched for is at the left side of the segment
	setIsAtTheRight: boolean;//true when the set searched for is at the right side of the segment
	i0: number
	i1: number
}


interface TraversalGraphNode {
	/**@property the index of the vertex that this node represents */
	vertexIndex: number,
	/** @property an ordered collection of segments (left to right) that arrive from above the vertex represented by this node. Undefined when such segments do not exist*/
	arrivals: BinarySearchTree<SetSegment>,
	/** @property an ordered collection of segments (left to right) that depart downwards from the vertex represented by this node. Undefined when such segments do not exist*/
	departures: BinarySearchTree<SetSegment>
}
class TraversalGraph {
	/**
	 * @property 
	 * list of all nodes(vertices with segment information) in the same order as the sweepline events (including the intersections found)
	 */
	public nodes: TraversalGraphNode[];
	/**@property
	* maps vertex indices to node indices 
	*/
	public nodeMap: { [key: number]: number };
	/**@property
	* list of all node indices that reference nodes without segments that arrive from above
	 */
	public startNodes: Set<number>;
	/**@property
	* list of all node indices that reference nodes without segments that depart downwards
	 */
	public endNodes: Set<number>;

	public constructor() {
		this.nodes = [];
		this.nodeMap = {};
		this.startNodes = new Set();
		this.endNodes = new Set();
	}

	public addVertex(eventVertexIndex: number, emittedSegments: Segment[], vertices: Point2D[]): void {
		const node: TraversalGraphNode = {
			vertexIndex: eventVertexIndex,
			arrivals: emittedSegments.length > 0 ? new BinarySearchTree<SetSegment>(segmentHorizontalPositionComparator.bind(this, vertices)) : undefined,
			departures: undefined
		}
		const nodeIndex = this.nodes.length;
		this.nodes.push(node);
		this.nodeMap[node.vertexIndex] = nodeIndex;

		if (node.arrivals) {
			this.endNodes.add(nodeIndex);
		} else {
			this.startNodes.add(nodeIndex);
		}
		if (node.arrivals) {
			//this node has arriving segments that departed from another node. We now add the arriving segments and  hook them up to these departure nodes
			emittedSegments.forEach(segment => {
				const setSegment: SetSegment = Object.assign({ leftSets: [], rightSets: [] }, segment);
				node.arrivals.add(setSegment);
				const departureNodeIndex = this.nodeMap[setSegment.iVertical0];
				const departureNode = this.nodes[departureNodeIndex];
				//the departureNode has at least one departing segment so cannot be an end node
				this.endNodes.delete(departureNodeIndex);
				if (departureNode.departures === undefined) {
					departureNode.departures = new BinarySearchTree<SetSegment>(segmentHorizontalPositionComparator.bind(this, vertices));
				}
				//add this segment to the collection of departing segments
				departureNode.departures.add(setSegment);
			});
		}
	}

	public applyDistribution(vertices: Point2D[]): void {
		const sweepLine = new BinarySearchTree<SetSegment>(segmentHorizontalPositionComparator.bind(this, vertices));
		this.nodes.forEach(node => {
			//			console.log(`applying distibution for vertex ${
			//				node.vertexIndex
			//				}: arrivals=${
			//					node.arrivals ? node.arrivals.size(): 0
			//				}, departures=${
			//					node.departures ? node.departures.size() : 0
			//				}`);

			if (node.arrivals) {
				this.setDistrubution(sweepLine);
				sweepLine.deleteAll(node.arrivals);
				//				const emitted = sweepLine.deleteAll(node.arrivals);
				//     			console.log('emitted: '+emitted.map(segment => `${'('+segment.leftSets.join(',')+')'}  ${segment.iVertical0}->${segment.iVertical1}  ${'('+segment.rightSets.join(',')+')'}`).join(', '));
			}
			if (node.departures) {
				sweepLine.addAll(node.departures);
			}
			//   			console.log('finished: sweepline= '+Array.from(sweepLine.inOrder()).map(segment => `${'('+segment.leftSets.join(',')+')'}  ${segment.iVertical0}->${segment.iVertical1}  ${'('+segment.rightSets.join(',')+')'}`).join(', '));
		});
	}

	/**
	* This method determines which polygons/sets lie at the left / right side of each of the given segments 
	* on the sweepLine. At  the start each segment only has information to which polygon/set it originally 
	* belongs (will be multiple polygons when the segment is shared by more then one polygon). After this 
	* call each segment also has information regarding all other polygons. E.g. when a polygon lies within 
	* another polygon. We call this the distribution of polygons over the given sweepline
	 */
	private setDistrubution(sweepLine: BinarySearchTree<SetSegment>): Set<number>[] {
		const inOrderSegments = Array.from(sweepLine.inOrder());

		const distribution: Set<number>[] = [...new Array(sweepLine.size() + 1)].map(() => new Set());

		const segmentsSetSides: number[][][] =
			inOrderSegments.map(segment =>
				Object.entries(segment.setSides).reduce((sides, entry) => {
					sides[entry[1]].push(Number.parseInt(entry[0]));
					return sides;
				}, <number[][]>[[], []]));

		let carry = new Set<number>();
		for (let segmentIndex = 0; segmentIndex < segmentsSetSides.length; segmentIndex++) {
			const setSides = segmentsSetSides[segmentIndex];
			//add sets at the RIGHT side of the segment to the carry set
			setSides[Side.RIGHT].forEach(setId => carry.add(setId));
			//remove sets at the LEFT side of the segment from carry set
			setSides[Side.LEFT].forEach(setId => carry.delete(setId));
			//add the contents of the carry set to the distribution slot that corresponds to the RIGHT side of current segment
			inOrderSegments[segmentIndex].rightSets.length = 0;
			carry.forEach(setId => {
				distribution[segmentIndex + 1].add(setId)
			});
		}

		carry.clear()
		for (let segmentIndex = segmentsSetSides.length - 1; segmentIndex >= 0; segmentIndex--) {
			const setSides = segmentsSetSides[segmentIndex];
			//add sets at the LEFT side of the segment to the carry set
			setSides[Side.LEFT].forEach(setId => carry.add(setId));
			//remove sets at the RIGHT side of the segment from carry set
			setSides[Side.RIGHT].forEach(setId => carry.delete(setId));
			//add the contents of the carry set to the distribution slot that corresponds to the LEFT side of current segment (when not already in)
			inOrderSegments[segmentIndex].leftSets.length = 0;
			carry.forEach(setId => {
				distribution[segmentIndex].add(setId)
			});
		}

		inOrderSegments.forEach((segment, index) => {
			segment.leftSets = Array.from(distribution[index]);
			segment.rightSets = Array.from(distribution[index + 1]);
		});

		return distribution;
	}


	public getEdges(setSelector: SetSelector): SetEdge[] {
		return this.nodes.flatMap(node => node.departures ? Array.from(node.departures.inOrder()) : <SetSegment[]>[])
			.map(setSegment => <SetEdge>Object.assign({ setIsAtTheLeft: setSelector(setSegment.leftSets), setIsAtTheRight: setSelector(setSegment.rightSets), i0: undefined, i1: undefined }, setSegment))
			.filter(setEdge =>
				//a line is an edge if one side is part of the set and the other side is not.
				setEdge.setIsAtTheLeft != setEdge.setIsAtTheRight
			).map(setEdge => {
				setEdge.i0 = setEdge.setIsAtTheLeft ? setEdge.iVertical1 : setEdge.iVertical0;
				setEdge.i1 = setEdge.setIsAtTheLeft ? setEdge.iVertical0 : setEdge.iVertical1;
				return setEdge
			});
	}


	public toString(): string {
		return 'TraversalGraph:\n' + this.nodes.map(node => `${node.arrivals ? Array.from(node.arrivals.inOrder()).map(segment => '[' + segment.leftSets.join(',') + ']' + segment.iVertical0 + '[' + segment.rightSets.join(',') + ']').join(' | ') : 'EMPTY'
			}  --> (${node.vertexIndex})  --> ${node.departures ? Array.from(node.departures.inOrder()).map(segment => '[' + segment.leftSets.join(',') + ']' + segment.iVertical1 + '[' + segment.rightSets.join(',') + ']').join(' | ') : 'EMPTY'
			}`).join('\n');
	}
}

